import { FormControlStatus } from '@angular/forms';
import { FormFieldConfig, GroupedFormData } from '@navstation-portal/reporting/data-model/ui';
import { FormSectionState, FormState, StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { isStateField } from './vm-builder-helpers';

/**
 * A field value counts as "empty" when the user has provided nothing meaningful.
 * `0` and `false` are deliberately NOT empty (they are valid user input).
 */
export const isEmptyValue = (value: StateFieldType): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/**
 * True when a number or a date sits below `min` or above `max`, as the number
 * input and the date input check it. An empty value is never outside a bound.
 */
export const isOutsideBounds = (
  value: StateFieldType,
  min: number | Date | undefined,
  max: number | Date | undefined
): boolean => {
  if (typeof value !== 'number' && !(value instanceof Date)) return false;
  // A date compares by its time.
  const actual = Number(value);
  if (Number.isNaN(actual)) return false;
  return (min !== undefined && actual < Number(min)) || (max !== undefined && actual > Number(max));
};

/**
 * Data-driven field validity: a pure function of the field value and its
 * {@link FormFieldConfig}, so validity can be computed in the store without
 * rendering the tab.
 *
 * Covers exactly what the rules payload states — `isVisible` and `isRequired` —
 * and nothing else. It is NOT equivalent to what the template validators produce
 * once the field is rendered, in either direction:
 *
 * - Required-ness is hardcoded or store-derived in several templates and absent
 *   from the rules (`[required]="true"` in Fuel Specs, `[required]="showDebunkered"`
 *   in Debunkered), so a field can be stamped VALID and render INVALID.
 * - Constraints the rules don't carry (`[min]`, `[max]`) are not checked here.
 * - Templates that render a field conditionally on something other than
 *   `isVisible` (e.g. Cargo Weight in operational Miscellaneous) can render a
 *   field as not-required that is stamped INVALID.
 * - For array-of-row sections the one section-level rule applies to every row,
 *   so a row-0-only template rule (Fuel Oil Bunkers) diverges from row 1 up.
 *
 * This is the single place to extend as more constraints move onto
 * `FormFieldConfig` from the backend — add them here and every caller (initial
 * stamping, future config-driven inputs) picks them up.
 */
export const validateField = (value: StateFieldType, config: FormFieldConfig | undefined): FormControlStatus => {
  // No rule for this field: nothing to validate against, treat as valid (matches
  // the `isFieldRequired` default of `false`).
  if (!config) return 'VALID';

  // Hidden fields are excluded from validity.
  if (config.isVisible === false) return 'VALID';

  if (config.isRequired && isEmptyValue(value)) return 'INVALID';

  return 'VALID';
};

/**
 * Returns a copy of `section` with every field's `status` recomputed from the
 * section's rules via {@link validateField}. Handles both plain fields and
 * array-of-row sections (e.g. bunker rows) the same way `isSectionValid` walks
 * them.
 */
const stampSectionStatuses = (
  section: FormSectionState,
  sectionRules: FormFieldConfig[] | undefined
): FormSectionState => {
  // No rules for this section (a normal case — it's what `isSectionVisible` keys
  // on): nothing to judge against, so leave the statuses as they are rather than
  // asserting VALID. Mirrors the tab-level early return in stampInitialStatuses.
  if (!sectionRules) return section;

  const rulesByFieldId = new Map(sectionRules.map((rule) => [rule.fieldId, rule]));
  const ruleFor = (fieldId: string) => rulesByFieldId.get(fieldId);

  const stamped: FormSectionState = {};

  for (const [fieldId, entry] of Object.entries(section)) {
    if (isStateField(entry)) {
      stamped[fieldId] = { ...entry, status: validateField(entry.value, ruleFor(fieldId)) };
      continue;
    }

    // Array-of-rows section: stamp each nested field per row.
    stamped[fieldId] = entry.map((row) => {
      const stampedRow: (typeof entry)[number] = {};
      for (const [nestedFieldId, nestedField] of Object.entries(row)) {
        stampedRow[nestedFieldId] = {
          ...nestedField,
          status: validateField(nestedField.value, ruleFor(nestedFieldId)),
        };
      }
      return stampedRow;
    });
  }

  return stamped;
};

/**
 * Returns a copy of `formState` with every field's `status` recomputed from
 * `tabRules`. Used at report initialisation so a tab reports accurate validity
 * (tab tick marks, Submit gating) before it has ever been rendered.
 */
export const stampInitialStatuses = (formState: FormState, tabRules: GroupedFormData | undefined): FormState => {
  if (!tabRules) return formState;

  const stamped: FormState = {};
  for (const [sectionId, section] of Object.entries(formState)) {
    stamped[sectionId] = stampSectionStatuses(section, tabRules[sectionId]);
  }

  return stamped;
};
